/**
 * Navbar — Streamlined sticky navigation with Legacy Triangle branding
 * Design: Navy background, orange Client Login button, green Apply Now CTA
 */

import React, { useState, useEffect, useCallback } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/pxebeeu2_Legacy%20triangle%20logo.png";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Funding Options", href: "/funding-options" },
  { label: "Commercial RE Lending", href: "/commercial-re-lending" },
  { label: "Funding Blueprint", href: "/funding-blueprint" },
  { label: "Business Credit Builder", href: "/business-credit-builder" },
  { label: "Blog", href: "/blog" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [lastScrollY, setLastScrollY] = useState(0);

  const onScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    setScrolled(currentScrollY > 40);
    
    if (currentScrollY > 100 && currentScrollY > lastScrollY) {
      setIsMinimized(true);
    } else if (currentScrollY < 100 || currentScrollY < lastScrollY) {
      setIsMinimized(false);
    }
    
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0f2d52] shadow-xl" : "bg-[#0f2d52]/95 backdrop-blur-sm"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-300 ${isMinimized ? "py-2 md:py-2" : "py-3 md:py-4"}`}>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group flex-shrink-0" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Legacy Triangle Logo"
            className={`transition-all duration-300 ${isMinimized ? "h-10 w-10 sm:h-12 sm:w-12" : "h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16"}`}
          />
          <div className="flex flex-col leading-tight">
            <span
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className={`text-white font-bold tracking-tight transition-all duration-300 ${isMinimized ? "text-xs sm:text-sm" : "text-sm sm:text-base lg:text-lg"}`}
            >
              Legacy Triangle
            </span>
            <span
              className={`text-[#48d480] font-bold tracking-widest uppercase transition-all duration-300 ${isMinimized ? "text-[0.5rem] sm:text-[0.6rem]" : "text-[0.55rem] sm:text-xs"}`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Start, Fund & Grow
            </span>
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden xl:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                isActive(link.href)
                  ? "text-[#48d480]"
                  : "text-white/80 hover:text-white"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {link.label}
              {link.comingSoon && (
                <span className="text-[9px] bg-[#e8821c] text-white px-1.5 py-0.5 rounded-full font-bold uppercase">
                  Soon
                </span>
              )}
            </Link>
          ))}
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden xl:flex items-center gap-3">
          <Link
            to="/apply"
            data-testid="apply-now-btn"
            className="flex items-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-colors duration-200 shadow-lg shadow-orange-900/30"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Apply Now
            <ChevronRight size={15} />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="xl:hidden text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="xl:hidden bg-[#091e38] border-t border-white/10 px-4 pb-6 pt-2" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center justify-between py-3 text-sm font-medium border-b border-white/10 transition-colors ${
                isActive(link.href)
                  ? "text-[#48d480]"
                  : "text-white/80 hover:text-[#48d480]"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {link.label}
              {link.comingSoon && (
                <span className="text-[9px] bg-[#e8821c] text-white px-1.5 py-0.5 rounded-full font-bold uppercase">
                  Coming Soon
                </span>
              )}
            </Link>
          ))}

          {/* Mobile CTA Buttons */}
          <div className="flex flex-col gap-3 mt-5">
            <Link
              to="/apply"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 bg-[#e8821c] hover:bg-[#d67518] text-white text-sm font-bold py-3 rounded-lg transition-colors"
            >
              Apply Now
              <ChevronRight size={15} />
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
